<template>
    <div ref="container" class="background-magic"></div>
</template>

<script>
import { markRaw } from "vue";
import * as THREE from "three";

export default {
    name: "GridWarp",
    data() {
        return {
            threeScene: null,
            threeCamera: null,
            threeRenderer: null,
            threePlane: null,
            originalPositions: null,
            mouse: markRaw(new THREE.Vector2()),

            // ‣ You can tweak these to adjust how “fish-eyed” the warp is:
            warpHeight: 5,   // overall amplitude (peak) of each ring
            warpSigma: 2,    // how wide/soft each ring is
            squareSegments: 15, // number of cells along the short side
            gridColor: 0x000000, // grid line color

            // NEW: ripple/delay parameters
            ripples: [],             // stores { x, z, startTime }
            rippleSpeed: 50,         // world units per second (ring expansion speed)
            maxRippleAge: 4000,      // ms before a ripple is removed
            rippleDelay: 80,         // ms to ramp from 0%→100% amplitude
        };
    },
    mounted() {
        // Wait until container is full‐viewport, then init Three.js
        this.$nextTick(() => {
            this.initThree();
        });

        // Listen globally so z-index doesn’t block mouse events
        window.addEventListener("mousemove", this.onMouseMove);
        window.addEventListener("resize", this.onWindowResize);
    },
    beforeUnmount() {
        window.removeEventListener("mousemove", this.onMouseMove);
        window.removeEventListener("resize", this.onWindowResize);
        if (this.threeRenderer && this.threeRenderer.domElement) {
            this.threeRenderer.domElement.remove();
        }
    },
    methods: {
        initThree() {
            // 1) Scene
            this.threeScene = markRaw(new THREE.Scene());

            // 2) Camera: sit at (0, 200, 0), looking straight down at y=0
            const w = this.$refs.container.clientWidth;
            const h = this.$refs.container.clientHeight;
            this.threeCamera = markRaw(
                new THREE.PerspectiveCamera(45, w / h, 0.1, 1000)
            );
            this.threeCamera.position.set(0, 200, 0);
            this.threeCamera.lookAt(0, 0, 0);

            // 3) Renderer
            this.threeRenderer = markRaw(
                new THREE.WebGLRenderer({ antialias: true, alpha: true })
            );
            this.threeRenderer.setPixelRatio(window.devicePixelRatio);
            this.threeRenderer.setSize(w, h);
            this.$refs.container.appendChild(this.threeRenderer.domElement);

            // 4) Build the fullscreen plane
            this.buildPlaneGeometry();

            // 5) Kick off the render+update loop
            this.animateThree();
        },

        buildPlaneGeometry() {
            // If there's already a plane, dispose of it
            if (this.threePlane) {
                this.threePlane.geometry.dispose();
                this.threeScene.remove(this.threePlane);
                this.threePlane = null;
            }

            // Compute how large the plane must be at y=0 to fill the camera’s view
            const width = this.$refs.container.clientWidth;
            const height = this.$refs.container.clientHeight;
            const d = Math.abs(this.threeCamera.position.y);
            const vFOV = THREE.MathUtils.degToRad(this.threeCamera.fov);
            const planeHeight = 2 * Math.tan(vFOV / 2) * d;
            const planeWidth = planeHeight * (width / height);

            // Determine segment count so each cell is square:
            // Use squareSegments along the Z axis (short side), then compute X segments accordingly.
            const segmentsZ = this.squareSegments;
            const cellSize = planeHeight / segmentsZ;
            const segmentsX = Math.round(planeWidth / cellSize);

            const geo = new THREE.PlaneGeometry(
                planeWidth,
                planeHeight,
                segmentsX,
                segmentsZ
            );
            // Rotate so local Y → world Y (plane lies flat on XZ)
            geo.rotateX(-Math.PI / 2);

            // Snapshot all original vertex positions
            this.originalPositions = Float32Array.from(geo.attributes.position.array);

            // Wireframe material for the grid lines
            const mat = new THREE.MeshBasicMaterial({
                color: this.gridColor,
                wireframe: true,
            });
            this.threePlane = markRaw(new THREE.Mesh(geo, mat));
            this.threeScene.add(this.threePlane);
        },

        animateThree() {
            // 1) Remove old ripples
            this.updateRipples();

            // 2) Warp the grid based on all active ripples
            this.applyWarpRipples();

            // 3) Render
            this.threeRenderer.render(this.threeScene, this.threeCamera);

            // 4) Loop
            requestAnimationFrame(() => this.animateThree());
        },

        onWindowResize() {
            if (!this.threeCamera || !this.threeRenderer) return;

            const width = this.$refs.container.clientWidth;
            const height = this.$refs.container.clientHeight;
            this.threeCamera.aspect = width / height;
            this.threeCamera.updateProjectionMatrix();
            this.threeRenderer.setSize(width, height);
            this.buildPlaneGeometry();
        },

        onMouseMove(event) {
            // Compute normalized device coords (NDC) relative to container
            const rect = this.$refs.container.getBoundingClientRect();
            this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            // Raycast from camera through that NDC to y=0 plane
            const vector = new THREE.Vector3(this.mouse.x, this.mouse.y, 0.5).unproject(
                this.threeCamera
            );
            const dir = vector.sub(this.threeCamera.position).normalize();
            const t = -this.threeCamera.position.y / dir.y;
            const intersect = this.threeCamera.position
                .clone()
                .add(dir.multiplyScalar(t));

            // Spawn a new ripple at that (x,z), timestamped
            this.ripples.push({
                x: intersect.x,
                z: intersect.z,
                startTime: performance.now(),
            });
        },

        updateRipples() {
            const now = performance.now();
            // Discard ripples older than maxRippleAge:
            this.ripples = this.ripples.filter(
                (r) => now - r.startTime < this.maxRippleAge
            );
        },

        applyWarpRipples() {
            if (!this.threePlane) return;

            const posArray = this.threePlane.geometry.attributes.position.array;
            const len = posArray.length;
            const h = this.warpHeight;
            const s = this.warpSigma;
            const now = performance.now();

            // For each vertex, sum contributions from every ripple
            for (let i = 0; i < len; i += 3) {
                const vx = this.originalPositions[i];
                const vz = this.originalPositions[i + 2];

                let totalOffset = 0;
                for (const ripple of this.ripples) {
                    const ageMs = now - ripple.startTime;
                    // Compute normalized ramp [0..1] over rippleDelay
                    const ramp = Math.min(ageMs / this.rippleDelay, 1);
                    // Compute current ring radius
                    const radius = this.rippleSpeed * (ageMs / 1000);
                    // Distance from this vertex to ripple center
                    const dx = vx - ripple.x;
                    const dz = vz - ripple.z;
                    const dist = Math.sqrt(dx * dx + dz * dz);

                    // Gaussian ring: peak when dist ≈ radius, scaled by ramp
                    const d = dist - radius;
                    const ring = h * Math.exp(-(d * d) / (2 * s * s)) * ramp;
                    totalOffset += ring;
                }

                // Assign computed Y offset
                posArray[i + 1] = totalOffset;
            }

            // Inform Three.js that positions changed
            this.threePlane.geometry.attributes.position.needsUpdate = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.background-magic {
    /* Full‐screen coverage */
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100%;
    z-index: 0;

    /* Force Three.js canvas to fill this div */
    ::v-deep canvas {
        display: block;
        width: 100%;
        height: 100%;
        pointer-events: none;
    }
}
</style>
